import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { cn } from '@clarix/ui';
import { useAuthStore } from '@clarix/auth-store';
import { useMe } from '@/core/api';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { APP_REGISTRY } from '@/gateway/apps';
import { getAccessibleApps } from '@/gateway/accessibleApps';
import { AppMark } from '@/gateway/AppMark';

/**
 * Rail app switcher — grid launcher.
 *
 * Rendered at the bottom of the SidebarRail: an "Apps" node that, on
 * hover/focus, opens a rectangular panel of the accessible apps laid out in
 * rows and columns, each a rounded-square brand mark with its name beneath.
 * The panel flies out to the RIGHT of the rail (the only direction with room)
 * and carries the same tinted, blurred surface the rest of the rail chrome
 * uses. Clicking opens the app in a NEW TAB so the destination boots with a
 * fresh runtime — no Redux/query-cache state from the previous app bleeds in.
 *
 * This replaced a radial fan: the arc packed long names ("Performance
 * Management") into overlapping labels and grew unreadable past ~4 apps, while
 * a grid stays legible however many are enabled.
 *
 * "Can access" rules:
 *   - admin       — gated to workspace OWNER + platform admins
 *   - other apps  — surfaced when the active workspace has the app enabled
 *                   (read from /me's active_workspace_apps map)
 */

/** Columns in the grid. Past this the panel wraps to a new row, so the panel
 *  grows downwards rather than off the side of the viewport. */
const MAX_COLUMNS = 4;
/** Per-item stagger for the open transition. */
const STAGGER_MS = 45;
/** Grace period before the panel collapses after the pointer leaves. */
const CLOSE_DELAY_MS = 250;

export function AppSwitcher() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const role = useAuthStore((s) => s.user?.workspace_role);

  // Source enabled apps from /me's live `active_workspace_apps` map rather
  // than the JWT claim — the JWT is only re-issued at login/workspace-switch,
  // so /admin/apps toggles wouldn't surface here until the next page load.
  // useMe is keyed off the same activeSlug as WorkspaceShell's bootstrap call,
  // so this just reads from the existing cache (no extra request).
  const urlSlug = useActiveWorkspaceSlug();
  const me = useMe({ activeSlug: urlSlug ?? undefined });

  // Active app = first non-empty path segment. Match against APP_REGISTRY.slug.
  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const active = APP_REGISTRY.find((a) => a.slug === firstSegment);

  // Filter to apps the workspace + user can access (shared with the AppLauncher
  // landing page so both stay in sync).
  const visibleApps = getAccessibleApps(me.data, {
    role,
    activeSlug: urlSlug ?? undefined,
  });

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const openNow = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  if (visibleApps.length === 0) return null;

  const columns = Math.min(MAX_COLUMNS, visibleApps.length);
  // Column count is runtime data (it follows how many apps are enabled), so it
  // rides on a CSS variable rather than a literal class — UI rule 1.
  const gridVars = { '--app-cols': String(columns) } as CSSProperties;

  return (
    <div
      className="relative flex flex-col items-center gap-0.5"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      onFocus={openNow}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      {/* Panel — opens to the RIGHT of the rail and grows upward from the Apps
          node, so it never covers the node itself or runs off the bottom. */}
      <div
        className={cn(
          // w-max is load-bearing: this is absolutely positioned inside the
          // 64px rail, so without it the shrink-to-fit width is capped by that
          // containing block, the 1fr columns collapse to nothing and the
          // fixed-width cells overlap each other.
          'absolute bottom-0 left-full z-50 ml-3 w-max',
          !open && 'pointer-events-none',
        )}
      >
        <div
          className={cn(
            'origin-bottom-left rounded-2xl p-3 shadow-xl',
            // ── Panel surface — TWO options under review, keep exactly one ──
            // A) Plain white (currently live).
            'bg-surface border-border border',
            // B) Tinted + blurred, as the radial fan had it. To switch back,
            //    comment out (A) above and uncomment BOTH lines below.
            // 'backdrop-blur-sm',
            // 'to-background/30 bg-gradient-to-tr from-indigo-100/70 via-indigo-50/60',
            // ease-spring (preset: cubic-bezier(.16,1,.3,1)) leaves fast and
            // settles slowly — the panel arrives rather than fades in. It does
            // NOT overshoot; the tiles below do that. motion-reduce jumps to
            // the end state.
            'ease-spring transition-all duration-500 motion-reduce:transition-none',
            open ? 'translate-x-0 scale-100 opacity-100' : '-translate-x-3 scale-90 opacity-0',
          )}
        >
          <div
            style={gridVars}
            className="grid gap-x-1 gap-y-3 [grid-template-columns:repeat(var(--app-cols),minmax(0,1fr))]"
          >
            {visibleApps.map((app, i) => {
              const isActive = app.slug === active?.slug;
              // Display-only apps (no shell remote/route) are shown but not
              // openable — navigating would hit the router catch-all and open
              // the wrong app.
              const openable = !app.displayOnly;
              // getAccessibleApps overlays the BACKEND registry name onto the
              // static one, which is right for the launcher card and the tab
              // title but too long for a 60px rail cell — PMS registers as
              // 'Performance Management'. The rail shows the shell's own short
              // product name; every other app names itself identically in both
              // places, so this changes only the long ones.
              const railName = APP_REGISTRY.find((r) => r.slug === app.slug)?.name ?? app.name;
              const itemVars = { '--d': `${i * STAGGER_MS}ms` } as CSSProperties;
              return (
                <div
                  key={app.id}
                  style={itemVars}
                  className={cn(
                    'flex w-[60px] flex-col items-center gap-1.5',
                    // ease-spring, not an arbitrary back-ease: a literal
                    // `ease-[cubic-bezier(...)]` does NOT compile here (verified
                    // against a real Tailwind build — it emits nothing and warns
                    // the brackets look like content). Liveliness comes from the
                    // stagger + scale instead of an overshoot curve.
                    'ease-spring transition-all duration-500 [transition-delay:var(--d)]',
                    'motion-reduce:transition-none motion-reduce:[transition-delay:0ms]',
                    open
                      ? 'translate-y-0 scale-100 opacity-100'
                      : 'translate-y-2 scale-90 opacity-0',
                  )}
                >
                  <button
                    type="button"
                    tabIndex={open ? 0 : -1}
                    aria-disabled={!openable}
                    aria-label={
                      isActive
                        ? `${app.name} (current app)`
                        : openable
                          ? `Open ${app.name} in a new tab`
                          : `${app.name} — coming soon, not available yet`
                    }
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => {
                      setOpen(false);
                      // New tab on purpose: the destination app boots in a fresh
                      // document, so nothing cached from the current app (stores,
                      // query caches, MF remote state) carries over.
                      if (!isActive && openable)
                        window.open(`/${app.slug}`, '_blank', 'noopener,noreferrer');
                    }}
                    className={cn(
                      // The mark fills the whole tile — no inset badge on a pad,
                      // which left a ring of background around the app's colour.
                      'flex h-8 w-8 items-center justify-center overflow-hidden rounded-[9px]',
                      'shadow-md',
                      'transition-transform duration-150 ease-out',
                      openable
                        ? 'hover:z-10 hover:scale-110 hover:shadow-lg focus-visible:scale-110'
                        : 'cursor-not-allowed opacity-60',
                      'focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-2',
                      isActive && 'ring-primary/60 ring-2',
                    )}
                  >
                    <AppMark app={app} className="h-full w-full rounded-[9px]" />
                  </button>
                  {/* App name — wraps to at most two lines so a long product
                      name ("Performance Management") never widens the column. */}
                  <span
                    className={cn(
                      'pointer-events-none line-clamp-2 select-none text-center text-[10.5px] font-medium leading-tight',
                      isActive ? 'text-primary' : 'text-foreground/75',
                    )}
                  >
                    {railName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Center node — styled like the other rail items. */}
      <button
        type="button"
        aria-label="Switch app"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openNow())}
        className={cn(
          'relative z-50 flex h-9 w-9 items-center justify-center rounded-lg',
          'transition-all duration-200',
          open
            ? 'text-primary rotate-90 opacity-100'
            : 'text-[color:var(--sr-icon)] opacity-60 hover:opacity-100',
        )}
      >
        <LayoutGrid className="h-5 w-5" strokeWidth={1.5} />
      </button>
      <span
        className={cn(
          'pointer-events-none select-none text-xs font-medium leading-none tracking-[0.01em]',
          open
            ? 'text-primary relative z-50 opacity-100'
            : 'text-[color:var(--sr-icon)] opacity-60',
        )}
      >
        Apps
      </span>
    </div>
  );
}
